import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useUserUpdateNameMutation } from "services/graphql";
import { checkNameValidity } from "helpers/checkNameValidity";
import { useOnboardingAnalytics } from "@/analytics";
import { UserData } from "../../constants";
import styles from "../../styles.module.scss";

const MAX_NAME_LENGTH = 25;
const DEBOUNCE_DELAY = 1000;

export interface NameStepProps {
  onComplete: (data: Pick<UserData, "name">) => void;
  initialValue?: string;
}

export const NameStep: React.FC<NameStepProps> = ({
  onComplete,
  initialValue,
}) => {
  const [name, setName] = useState(initialValue || "");
  const [debouncedName, setDebouncedName] = useState(name);
  const [error, setError] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [updateName] = useUserUpdateNameMutation();
  const debounceTimer = useRef<NodeJS.Timeout>();
  const {
    setNameAnalytics,
    onboardingErrorAnalytics,
  } = useOnboardingAnalytics();

  useEffect(() => {
    setIsTyping(true);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedName(name);
      setIsTyping(false);
      validateName(name);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [name]);

  const validateName = (text: string) => {
    const { isValid, message } = checkNameValidity(text);
    setError(message);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateName(debouncedName)) {
      return;
    }

    try {
      await updateName({
        variables: {
          input: {
            name: debouncedName.trim(),
          },
        },
      });
      setNameAnalytics(debouncedName.trim());
      onComplete({ name: debouncedName.trim() });
    } catch (err) {
      const errorMessage = "Failed to update name. Please try again.";
      setError(errorMessage);
      onboardingErrorAnalytics(errorMessage);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <h1 className={styles.title}>What&apos;s your name?</h1>

      <form onSubmit={handleSubmit}>
        <input
          type="text"
          value={name}
          onChange={e => {
            setName(e.target.value);
          }}
          className={styles.input}
          placeholder="Enter your name"
          maxLength={MAX_NAME_LENGTH}
        />

        <div className={styles.validityContainer}>
          {error && <p className={styles.error}>{error}</p>}
          <span className={styles.characterCount}>
            {name.length}/{MAX_NAME_LENGTH}
          </span>
        </div>

        <button
          type="submit"
          className={styles.button}
          disabled={!debouncedName.trim() || !!error || isTyping}
        >
          CONTINUE
        </button>
      </form>
    </motion.div>
  );
};
